// WP-3.4 (M4 SC-1..8, S-14): schedule item queries/mutations.
import {
  agingBucketsResponseSchema,
  scheduleItemListResponseSchema,
  scheduleItemResponseSchema,
  scheduleRollupsResponseSchema,
  type AgingBucketsResponse,
  type ScheduleItemCancelRequest,
  type ScheduleItemCreateRequest,
  type ScheduleItemEditRequest,
  type ScheduleItemRescheduleRequest,
  type ScheduleItemResponse,
  type ScheduleRollupsResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';

export const SCHEDULE_ITEMS_KEY = ['schedule-items'] as const;

export interface ScheduleItemFilters {
  projectId?: string;
  partyId?: string;
  confidence?: string;
}

function filterQuery(filters: ScheduleItemFilters): string {
  const params = new URLSearchParams();
  for (const key of Object.keys(filters) as (keyof ScheduleItemFilters)[]) {
    const value = filters[key];
    if (value !== undefined && value !== '') params.set(key, value);
  }
  const qs = params.toString();
  return qs === '' ? '' : `?${qs}`;
}

export function useScheduleItems(
  filters: ScheduleItemFilters = {},
): UseQueryResult<ScheduleItemResponse[]> {
  return useQuery({
    queryKey: [...SCHEDULE_ITEMS_KEY, filters],
    queryFn: async () =>
      scheduleItemListResponseSchema.parse(
        await api.get(`/api/v1/schedule-items${filterQuery(filters)}`),
      ).scheduleItems,
  });
}

export function useScheduleItem(id: string): UseQueryResult<ScheduleItemResponse> {
  return useQuery({
    queryKey: [...SCHEDULE_ITEMS_KEY, id],
    queryFn: async () =>
      scheduleItemResponseSchema.parse(await api.get(`/api/v1/schedule-items/${id}`)),
    enabled: id !== '',
  });
}

export function useScheduleRollups(currency: string): UseQueryResult<ScheduleRollupsResponse> {
  return useQuery({
    queryKey: [...SCHEDULE_ITEMS_KEY, 'rollups', currency],
    queryFn: async () =>
      scheduleRollupsResponseSchema.parse(
        await api.get(`/api/v1/schedule-items/rollups?currency=${currency}`),
      ),
    enabled: currency !== '',
  });
}

export function useAgingBuckets(currency: string): UseQueryResult<AgingBucketsResponse> {
  return useQuery({
    queryKey: [...SCHEDULE_ITEMS_KEY, 'aging', currency],
    queryFn: async () =>
      agingBucketsResponseSchema.parse(
        await api.get(`/api/v1/schedule-items/aging?currency=${currency}`),
      ),
    enabled: currency !== '',
  });
}

function useInvalidateScheduleItems(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: SCHEDULE_ITEMS_KEY });
  };
}

export function useCreateScheduleItem(): UseMutationResult<
  ScheduleItemResponse,
  Error,
  ScheduleItemCreateRequest
> {
  const invalidate = useInvalidateScheduleItems();
  return useMutation({
    mutationFn: async (input) =>
      scheduleItemResponseSchema.parse(await api.post('/api/v1/schedule-items', input)),
    onSuccess: invalidate,
  });
}

export function useEditScheduleItem(
  id: string,
): UseMutationResult<ScheduleItemResponse, Error, ScheduleItemEditRequest> {
  const invalidate = useInvalidateScheduleItems();
  return useMutation({
    mutationFn: async (input) =>
      scheduleItemResponseSchema.parse(await api.patch(`/api/v1/schedule-items/${id}`, input)),
    onSuccess: invalidate,
  });
}

export function useRescheduleScheduleItem(
  id: string,
): UseMutationResult<ScheduleItemResponse, Error, ScheduleItemRescheduleRequest> {
  const invalidate = useInvalidateScheduleItems();
  return useMutation({
    mutationFn: async (input) =>
      scheduleItemResponseSchema.parse(
        await api.post(`/api/v1/schedule-items/${id}/reschedule`, input),
      ),
    onSuccess: invalidate,
  });
}

export function useCancelScheduleItem(
  id: string,
): UseMutationResult<ScheduleItemResponse, Error, ScheduleItemCancelRequest> {
  const invalidate = useInvalidateScheduleItems();
  return useMutation({
    mutationFn: async (input) =>
      scheduleItemResponseSchema.parse(
        await api.post(`/api/v1/schedule-items/${id}/cancel`, input),
      ),
    onSuccess: invalidate,
  });
}
